import type { VaultOnChainData, ProtocolMetadata, SupSingleVaultV3, Networkish } from "@/types";
import axios, { type AxiosError, type AxiosResponse } from "axios";
import { getAuthToken } from "@dynamic-labs/sdk-react-core";

import useSWR, { type KeyedMutator } from "swr";
import { apiV2 } from "./http";
import { axiosRequestConfig } from "@/../config/swr.config";

export type SupVaultType = "ERC5115" | "ERC4626";
export type SupFormImplementationId = 1 | 5 | 3;

export interface ResponseCheckAddressVault {
  type: SupVaultType;
  form_implementation_id: SupFormImplementationId;
}

export function useGetProtocol(
  input: string | undefined,
  slug: string | number,
  isInputLoading: boolean
): {
  isLoading: boolean;
  isError: boolean;
  mutate: KeyedMutator<ProtocolMetadata>;
  data?: ProtocolMetadata;
} {
  const url = input ? `/protocol/${input}` : `/protocol/${slug}`;

  const fetcher = async (url: string): Promise<ProtocolMetadata> => {
    return await axios.get(url, axiosRequestConfig.v2).then((res: AxiosResponse) => res.data);
  };

  const { data, error, mutate } = useSWR(!isInputLoading ? url : null, fetcher);

  return {
    data: data,
    isLoading: !data && !error,
    isError: !!error,
    mutate,
  };
}

export function addProtocol(formData: unknown): Promise<{ status: boolean; data?: unknown; errorData?: string }> {
  const url = "/protocol";
  const fetcher = async (url: string) => {
    const instance = axios.create(axiosRequestConfig.v2);
    return await instance
      .post(url, formData)
      .then((res: AxiosResponse) => {
        return {
          status: true,
          data: res.data,
        };
      })
      .catch((err: AxiosError<{ error: string }>) => {
        console.warn("DEBUG: addProtocol error:", { error: err, message: err.message });
        return {
          status: false,
          errorData: err.response?.data.error,
        };
      });
  };

  return fetcher(url);
}

export function editProtocol(formData: Partial<ProtocolMetadata>): Promise<{ status: boolean; data?: unknown; errorData?: string }> {
  const url = "/protocol";
  const fetcher = async (url: string) => {
    const instance = axios.create(axiosRequestConfig.v2);
    return await instance
      .put(url, formData, { headers: { "SF-JWT": getAuthToken() } })
      .then((res: AxiosResponse) => {
        return {
          status: true,
          data: res.data,
        };
      })
      .catch((err: AxiosError<{ error: string }>) => {
        console.warn("DEBUG: editProtocol error:", { error: err, message: err.message });
        return {
          status: false,
          errorData: err.response?.data.error,
        };
      });
  };
  return fetcher(url);
}

export function editVault(formData: SupSingleVaultV3) {
  const url = "/vault";

  const fetcher = async (url: string) => {
    const instance = axios.create(axiosRequestConfig.v2);
    return await instance
      .put(url, formData, { headers: { "SF-JWT": getAuthToken() } })
      .then((res: AxiosResponse) => {
        return {
          status: true,
          data: res.data,
          errorData: undefined,
        };
      })
      .catch((err: AxiosError<{ error: string }>) => {
        console.warn("DEBUG: editVault error:", { error: err, message: err.message });
        return {
          status: false,
          data: undefined,
          errorData: err.response?.data.error,
        };
      });
  };

  return fetcher(url);
}

export function addVault(formData: SupSingleVaultV3) {
  const url = "/vault";

  const fetcher = async (url: string) => {
    const instance = axios.create(axiosRequestConfig.v2);
    return await instance
      .post(url, formData)
      .then((res: AxiosResponse) => {
        return {
          status: true,
          data: res.data,
          error: undefined,
        };
      })
      .catch((err: AxiosError<{ errors: { location: string; message: string; value: string }[] }>) => {
        console.warn("DEBUG: addVault error:", { error: err, message: err.message });
        return {
          status: false,
          data: undefined,
          error: err.response?.data.errors.map(e => e.message).join(", "),
        };
      });
  };

  return fetcher(url);
}

export async function checkVaultData(
  address: string,
  chain: Networkish,
  formImplementationId: SupFormImplementationId
): Promise<{ status: boolean; data?: VaultOnChainData; error?: string }> {
  return axios
    .get("/vault/onchain-data/", {
      ...axiosRequestConfig.v2,
      params: {
        contractAddress: address,
        chainID: chain,
        formImplementationID: formImplementationId,
      },
    })
    .then((res: AxiosResponse<VaultOnChainData>) => {
      return {
        status: true,
        data: res.data,
      };
    })
    .catch((err: AxiosError<{ error: string }>) => {
      return {
        status: false,
        error: err.response ? err.response.data.error : err.message || err.toString(),
      };
    });
}

export async function checkAddressVault(address: string, chain: number): Promise<{ status: boolean; data?: ResponseCheckAddressVault; error?: string }> {
  const url = `/checks/address/vault/?contract_address=${address}&chain_id=${chain}`;

  return axios
    .get(url, axiosRequestConfig.v2)
    .then((res: AxiosResponse<ResponseCheckAddressVault>) => {
      return {
        status: true,
        data: res.data,
      };
    })
    .catch((err: AxiosError<{ title: string; detail: string }>) => {
      console.log("err", err);
      return {
        status: false,
        error: err.response ? err.response.data.detail : err.message || err.toString(),
      };
    });
}

interface ApiParams<P = object, D = object> {
  url: string;
  method?: "GET" | "POST" | "DELETE" | "PUT";
  params?: P;
  data?: D;
}

export const swrFetcher = <R, P = object, D = object>({ method = "GET", url, params, data }: ApiParams<D, P>) => apiV2.request<P, R>({ method, url, params, data });
